import React, { useEffect, useState } from 'react';
import { API_BASE_URL, getAuthHeaders } from '../utils/api';
import './ResearchProjectPickerModal.css';

export default function ResearchProjectPickerModal({
  open,
  title = 'Select project',
  description = '',
  confirmLabel = 'Continue',
  onClose,
  onSelect,
}) {
  const [projects, setProjects] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) {
      setSelectedId('');
      setError('');
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const headers = await getAuthHeaders();
        const resp = await fetch(`${API_BASE_URL}/casc/v1/api/chat/project/list`, {
          method: 'POST',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify({ page: 1, limit: 100 }),
        });
        const json = await resp.json().catch(() => ({}));
        if (!resp.ok || !json?.success) {
          throw new Error(json?.error || json?.message || 'Failed to load projects');
        }
        if (!cancelled) {
          const items = json.data?.items || [];
          setProjects(items);
          if (items.length === 1) setSelectedId(items[0].project_id);
        }
      } catch (err) {
        if (!cancelled) setError(err?.message || 'Failed to load projects');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  if (!open) return null;

  const handleConfirm = async () => {
    if (!selectedId) {
      setError('Select a project');
      return;
    }
    const project = projects.find((p) => p.project_id === selectedId);
    setSubmitting(true);
    setError('');
    try {
      await onSelect({ projectId: selectedId, project });
    } catch (err) {
      setError(err?.message || 'Action failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="research-picker-overlay" role="presentation">
      <div
        className="research-picker-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="research-picker-title"
      >
        <header className="research-picker-header">
          <h2 id="research-picker-title">{title}</h2>
          {description ? <p>{description}</p> : null}
        </header>
        <div className="research-picker-body">
          {error ? <div className="research-picker-error" role="alert">{error}</div> : null}
          {loading ? <p className="research-picker-muted">Loading projects…</p> : null}
          {!loading && projects.length === 0 ? (
            <p className="research-picker-muted">No projects found. Create a project first.</p>
          ) : null}
          {!loading && projects.length > 0 ? (
            <label className="research-picker-label">
              Project
              <select value={selectedId} onChange={(e) => setSelectedId(e.target.value)}>
                <option value="">Choose a project…</option>
                {projects.map((p) => (
                  <option key={p.project_id} value={p.project_id}>{p.name || p.project_id}</option>
                ))}
              </select>
            </label>
          ) : null}
        </div>
        <footer className="research-picker-footer">
          <button type="button" className="research-picker-btn" onClick={onClose} disabled={submitting}>Cancel</button>
          <button
            type="button"
            className="research-picker-btn research-picker-btn--primary"
            onClick={handleConfirm}
            disabled={submitting || loading || !projects.length}
          >
            {submitting ? 'Working…' : confirmLabel}
          </button>
        </footer>
      </div>
    </div>
  );
}
